// The analysis engine's review page (D-195): twenty leads, for the person ae_control names and nobody else. It reads each lead
// as a caller would, with the running summary and what is known on top, then its calls in order; the transcript is there to
// check against. A verdict on a call is what tunes the playbook.
import { useState } from "react";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, Loading, Page, useLoad, useToast } from "../app/ui";
import type { ReviewCall, ReviewLead } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const COURSE: Record<string, string> = {
  autocad: "AutoCAD",
  revit: "Revit",
  "3ds_max": "3ds Max",
  staad_pro: "STAAD.Pro",
  etabs: "ETABS",
  estimation_costing: "Estimation and costing",
  building_design: "Building design",
  solidworks: "SolidWorks",
  catia: "CATIA",
  ansys: "ANSYS",
  nx_cad_cam: "NX CAD/CAM",
  autocad_electrical: "AutoCAD Electrical",
  revit_mep: "Revit MEP",
  primavera: "Primavera",
  ms_project: "MS Project",
  interior_design: "Interior design",
  fashion_design: "Fashion design",
  graphic_design: "Graphic design",
  photography: "Photography",
  solar: "Solar",
  other: "Other course",
};
const PLANNED: Record<string, string> = { centre_visit: "Centre visit planned", workshop: "Workshop planned" };
const CHECK: Record<string, string> = {
  introduced: "Introduced",
  asked_goal: "Asked their goal",
  suggested_course: "Suggested a course",
  proposed_next_step: "Proposed a next step",
  answered_concern: "Answered their concern",
  asked_to_enrol: "Asked them to enrol",
};
const NEXT: Record<string, string> = {
  send: "Send details",
  visit: "Centre visit",
  demo: "Demo or workshop",
  callback: "Call back",
};
const REACHED: Record<string, string> = {
  voicemail: "Voicemail",
  ivr: "Recorded message",
  wrong_person: "Wrong person",
  not_reached: "Not reached",
};

function Chips({ state }: { state: ReviewLead["state"] }) {
  const open = (state.objections ?? []) as { c: string }[];
  return (
    <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
      {state.interest && <span className={`badge ${state.interest}`}>{state.interest}</span>}
      {state.course && <span className="badge">{COURSE[state.course] ?? state.course}</span>}
      {state.mode && <span className="badge">{state.mode === "online" ? "Online" : "In class"}</span>}
      {state.planned && <span className="badge brand">{PLANNED[state.planned] ?? state.planned}</span>}
      {state.pay != null && <span className="badge">Willing to pay {state.pay} of 5</span>}
      {state.next?.reschedule && <span className="badge warm">Reschedule</span>}
      {state.considering != null && (
        <span className="badge warm">
          Still considering{state.considering > 1 ? `, ${state.considering} calls` : ""}
        </span>
      )}
      {state.customer_name && <span className="badge">Name on the call: {state.customer_name}</span>}
      {state.place && <span className="badge">{state.place}</span>}
      {state.background && <span className="badge">{state.background}</span>}
      {state.decides && <span className="badge">Decides: {state.decides}</span>}
      {state.start && <span className="badge">Start: {state.start}</span>}
      {open.map((o) => <span className="badge danger" key={o.c}>Open: {o.c}</span>)}
    </div>
  );
}

function CallRow({ call, feedback, tz, onVerdict }: {
  call: ReviewCall;
  feedback: ReviewCall["feedback"];
  tz: string;
  onVerdict: (right: boolean, note: string) => Promise<void>;
}) {
  const [note, setNote] = useState(feedback?.note ?? "");
  const [showText, setShowText] = useState(false);
  const r = call.record;
  const when = call.started_at ? fmtDateTime(call.started_at, tz) : "";
  const head = `${when} · ${call.direction ?? ""} · ${call.duration_s ?? "?"} s · ${call.caller ?? ""}`;
  if (!r) {
    return (
      <div className="muted" style={{ padding: "8px 0" }}>
        {head} · {call.status === "skipped" ? `not read (${call.skip_reason})` : call.status}
      </div>
    );
  }
  const next = Object.entries(
    (r.next ?? {}) as Record<string, { r?: boolean; when?: string | null; q?: string | null }>,
  )
    .filter(([k, a]) => k !== "any" && a?.r);
  const checks = ((r.checklist ?? []) as { k: string; r: string }[]).filter((c) => c.r !== "na");
  return (
    <div style={{ borderTop: "1px solid var(--line, #e5e5e0)", padding: "10px 0" }}>
      <div className="muted">{head}</div>
      {r.reached && r.reached !== "person" && (
        <div>
          <span className="badge">{REACHED[r.reached] ?? r.reached}</span>
        </div>
      )}
      {r.summary && <p style={{ margin: "4px 0" }}>{r.summary}</p>}
      {r.reschedule && (
        <p style={{ margin: "4px 0" }}>
          <span className="badge warm">Cannot make that date: reschedule</span>
        </p>
      )}
      {r.considering && (
        <p style={{ margin: "4px 0" }}>
          <span className="badge warm">Still considering</span>{" "}
          <span className="muted">"{r.values?.unavailable?.q}"</span>
        </p>
      )}
      {(r.values?.repeated?.length ?? 0) > 0 && (
        <p className="muted" style={{ margin: "4px 0" }}>Had to say again: {r.values?.repeated?.join(", ")}</p>
      )}
      {r.values?.unavailable?.v === "no_longer_wants" && (
        <p style={{ margin: "4px 0" }}>
          <span className="badge cold">No longer wants it</span>{" "}
          <span className="muted">"{r.values.unavailable.q}"</span>
        </p>
      )}
      {next.length > 0 && (
        <p style={{ margin: "4px 0" }}>
          <strong>Next:</strong> {next.map(([k, a]) => `${NEXT[k] ?? k}${a.when ? ` (${a.when})` : ""}`).join(", ")}
        </p>
      )}
      {r.reached === "person" && r.values?.interest && (
        <p className="muted" style={{ margin: "4px 0" }}>
          Interest {r.interest_after ?? r.values.interest.v}: "{r.values.interest.q}"
        </p>
      )}
      {checks.length > 0 && (
        <p className="muted" style={{ margin: "4px 0" }}>
          {checks.map((c) => `${c.r === "yes" ? "✓" : "✗"} ${CHECK[c.k] ?? c.k}`).join("  ")}
        </p>
      )}
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 6 }}>
        <button
          className={`small${feedback?.is_right === true ? " primary" : ""}`}
          onClick={() => void onVerdict(true, note)}
        >
          Right
        </button>
        <button
          className={`small${feedback?.is_right === false ? " primary" : ""}`}
          onClick={() => void onVerdict(false, note)}
        >
          Wrong
        </button>
        <input
          style={{ flex: 1, minWidth: 180 }}
          placeholder="What is wrong?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (note !== (feedback?.note ?? "") && feedback) void onVerdict(feedback.is_right, note);
          }}
        />
        <button className="small" onClick={() => setShowText(!showText)}>
          {showText ? "Hide transcript" : "Transcript"}
        </button>
      </div>
      {showText && <pre style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{call.transcript}</pre>}
    </div>
  );
}

export function AnalysisReview() {
  const { api, tz } = useMember();
  const { data, error, loading } = useLoad(() => api.analysisReview(), [api]);
  const toast = useToast();
  // A verdict shows at once and saves behind it: reloading twenty leads on each click looked as if nothing had happened.
  const [verdicts, setVerdicts] = useState<Record<string, ReviewCall["feedback"]>>({});

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  const verdict = async (id: string, right: boolean, note: string) => {
    const before = verdicts[id];
    setVerdicts((v) => ({ ...v, [id]: { is_right: right, note } }));
    try {
      await api.analysisFeedback(id, right, note);
    } catch (e) {
      setVerdicts((v) => ({ ...v, [id]: before }));
      toast(e instanceof Error ? e.message : "Could not save that.", true);
    }
  };
  const read = data.reduce((n, l) => n + l.calls.filter((c) => c.record).length, 0);
  const judged = data.reduce(
    (n, l) => n + l.calls.filter((c) => verdicts[c.recording_id] ?? c.feedback).length,
    0,
  );
  return (
    <Page
      title="Analysis review"
      sub={`Only you can see this. ${data.length} ${
        data.length === 1 ? "lead" : "leads"
      }; ${judged} of ${read} calls checked. Nothing here reaches a customer's page or a caller.`}
    >
      {data.length === 0 && (
        <Card>
          <Empty>No calls have been read yet.</Empty>
        </Card>
      )}
      <div className="stack">
        {data.map((lead) => (
          <Card key={lead.lead_id} title={lead.name ?? "No name"}>
            <div className="stack">
              {lead.state.summary && <p style={{ margin: 0, fontSize: "1.05em" }}>{lead.state.summary}</p>}
              <Chips state={lead.state} />
              <div>
                {lead.calls.map((call) => (
                  <CallRow
                    key={call.recording_id}
                    call={call}
                    feedback={verdicts[call.recording_id] ?? call.feedback}
                    tz={tz}
                    onVerdict={(right, note) => verdict(call.recording_id, right, note)}
                  />
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}
